/* Progressive collection browsing. Every request follows Shopify's real next-page URL. */
(() => {
  'use strict';
  if (window.__ronCollectionLoading) return;
  window.__ronCollectionLoading = true;

  const instances = new Map();
  const CACHE_PREFIX = 'ron:collection:v1:';
  const CACHE_LIFETIME = 10 * 60 * 1000;
  const MAX_CACHE_SIZE = 900000;
  const PRODUCT = '[data-product-id]';
  const EDITORIAL = '[data-ron-editorial-key]';
  const isBackNavigation = () => performance.getEntriesByType('navigation')[0]?.type === 'back_forward';

  class CollectionLoading {
    constructor(root) {
      this.root = root;
      this.grid = root.querySelector('[data-ron-collection-grid]');
      this.pagination = root.querySelector('[data-ron-pagination]');
      if (!this.grid) return;
      this.baseURL = new URL(location.href);
      this.page = Number(this.baseURL.searchParams.get('page') || 1);
      this.initialPage = this.page;
      this.total = Number(root.dataset.totalProducts || 0);
      this.seenProducts = new Set();
      this.seenEditorials = new Set();
      this.seenDestinations = new Set();
      this.loadedURLs = new Set();
      this.automaticRemaining = navigator.connection?.saveData ? 0 : 2;
      this.loading = false;
      this.suspended = false;
      this.controller = null;
      this.nextURL = this.validNextURL(this.pagination?.querySelector('a[rel="next"]')?.href);
      this.cacheKey = CACHE_PREFIX + (window.Shopify?.theme?.id || 'theme') + ':' + this.baseURL.pathname + this.baseURL.search;
      this.restore();
      this.indexExisting();
      this.createControls();
      this.form = root.querySelector('.ron-collection__filter-form');
      this.onSubmit = () => {
        this.suspend();
        this.removeCache();
      };
      this.form?.addEventListener('submit', this.onSubmit);
      this.observer = 'IntersectionObserver' in window ? new IntersectionObserver(entries => {
        if (entries.some(entry => entry.isIntersecting) && this.automaticRemaining > 0) this.load(false);
      }, { rootMargin: '0px 0px 550px 0px' }) : null;
      this.update();
      this.observe();
      instances.set(root, this);
    }

    validNextURL(value) {
      if (!value) return null;
      try {
        const url = new URL(value, this.baseURL);
        if (url.origin !== this.baseURL.origin || url.pathname !== this.baseURL.pathname) return null;
        const page = Number(url.searchParams.get('page'));
        if (!Number.isInteger(page) || page <= this.page) return null;
        // Shopify links carry active filters. Preserve the entry URL's complete query state too,
        // including repeated filter values and theme-preview parameters.
        const keys = new Set(this.baseURL.searchParams.keys());
        for (const key of keys) {
          if (key === 'page' || key === 'section_id') continue;
          url.searchParams.delete(key);
          this.baseURL.searchParams.getAll(key).forEach(value => url.searchParams.append(key, value));
        }
        url.searchParams.delete('section_id');
        url.hash = '';
        return url;
      } catch (_) {
        return null;
      }
    }

    indexExisting() {
      this.grid.querySelectorAll(PRODUCT).forEach(card => this.seenProducts.add(card.dataset.productId));
      this.grid.querySelectorAll(EDITORIAL).forEach(banner => {
        this.seenEditorials.add(banner.dataset.ronEditorialKey);
        if (banner.dataset.ronEditorialDestination) this.seenDestinations.add(banner.dataset.ronEditorialDestination);
      });
    }

    createControls() {
      this.controls = document.createElement('div');
      this.controls.className = 'ron-collection__load-controls';
      this.sentinel = document.createElement('span');
      this.sentinel.className = 'ron-collection__load-sentinel';
      this.sentinel.setAttribute('aria-hidden', 'true');
      this.status = document.createElement('p');
      this.status.className = 'ron-collection__load-status';
      this.status.setAttribute('role', 'status');
      this.status.setAttribute('aria-live', 'polite');
      this.status.setAttribute('aria-atomic', 'true');
      this.status.id = `${this.root.id}-loading-status`;
      this.button = document.createElement('button');
      this.button.type = 'button';
      this.button.className = 'ron-collection__load-more';
      if (!this.grid.id) this.grid.id = `${this.root.id}-products`;
      this.button.setAttribute('aria-controls', this.grid.id);
      this.button.setAttribute('aria-describedby', this.status.id);
      this.button.addEventListener('click', () => this.load(true));
      this.fallback = document.createElement('a');
      this.fallback.className = 'ron-collection__load-fallback';
      this.fallback.textContent = 'Go to the next page';
      this.fallback.hidden = true;
      this.controls.append(this.sentinel, this.status, this.button, this.fallback);
      if (this.initialPage > 1) {
        const first = new URL(this.baseURL);
        first.searchParams.delete('page');
        const startLink = document.createElement('a');
        startLink.className = 'ron-collection__load-start';
        startLink.href = first.href;
        startLink.textContent = 'Back to the first page';
        this.controls.append(startLink);
      }
      if (this.pagination) {
        this.pagination.after(this.controls);
        this.pagination.hidden = true;
      } else {
        this.grid.after(this.controls);
      }
      this.root.dataset.ronLoading = 'ready';
    }

    update(message = '') {
      const count = this.seenProducts.size;
      const countText = this.initialPage === 1 && this.total > 0
        ? `${count} of ${this.total} pieces shown.` : `${count} pieces shown.`;
      this.button.disabled = this.loading || this.suspended;
      this.button.hidden = !this.nextURL;
      this.button.textContent = this.loading ? 'Loading…' : 'Load more';
      this.grid.setAttribute('aria-busy', this.loading ? 'true' : 'false');
      this.status.textContent = message || (this.nextURL
        ? `${countText}${this.automaticRemaining === 0 ? ' Continue when you’re ready.' : ''}`
        : count ? `${countText} End of this selection.` : 'No pieces match this selection.');
      if (this.nextURL) this.fallback.href = this.nextURL.href;
    }

    observe() {
      this.observer?.disconnect();
      if (this.nextURL && this.automaticRemaining > 0 && !this.suspended && !this.loading) {
        this.observer?.observe(this.sentinel);
      }
    }

    async load(manual) {
      if (this.loading || this.suspended || !this.nextURL) return;
      const requestedURL = this.nextURL;
      if (this.loadedURLs.has(requestedURL.href)) return this.fail();
      if (manual) this.automaticRemaining = 0;
      this.loading = true;
      this.fallback.hidden = true;
      this.controller = new AbortController();
      const controller = this.controller;
      const timeout = setTimeout(() => controller.abort(), 12000);
      this.update('Loading the next pieces…');
      this.observer?.disconnect();
      try {
        const requestURL = new URL(requestedURL);
        if (this.root.dataset.sectionId) requestURL.searchParams.set('section_id', this.root.dataset.sectionId);
        const response = await fetch(requestURL.href, {
          signal: controller.signal,
          credentials: 'same-origin',
          headers: { Accept: 'text/html' }
        });
        if (!response.ok) throw new Error('Collection page unavailable');
        const html = await response.text();
        if (this.suspended || controller.signal.aborted || !this.root.isConnected) return;
        const parsed = new DOMParser().parseFromString(html, 'text/html');
        const incomingRoot = parsed.getElementById(this.root.id);
        const incomingGrid = incomingRoot?.querySelector('[data-ron-collection-grid]');
        if (!incomingGrid) throw new Error('Collection page not found');
        const fragment = document.createDocumentFragment();
        const newProducts = [];
        const newEditorials = [];
        const productIDs = new Set(this.seenProducts);
        const editorialKeys = new Set(this.seenEditorials);
        const destinations = new Set(this.seenDestinations);
        for (const item of Array.from(incomingGrid.children)) {
          if (item.matches(PRODUCT)) {
            const id = item.dataset.productId;
            if (!id || productIDs.has(id)) continue;
            productIDs.add(id);
            newProducts.push(item);
            fragment.append(item);
          } else if (item.matches(EDITORIAL)) {
            const key = item.dataset.ronEditorialKey;
            const destination = item.dataset.ronEditorialDestination;
            if (!key || editorialKeys.has(key) || (destination && destinations.has(destination))) continue;
            editorialKeys.add(key);
            if (destination) destinations.add(destination);
            newEditorials.push(item);
            fragment.append(item);
          }
        }
        // A repeated/malformed response must never generate an endless request loop.
        if (!newProducts.length) throw new Error('No new products returned');
        this.grid.append(fragment);
        this.seenProducts = productIDs;
        this.seenEditorials = editorialKeys;
        this.seenDestinations = destinations;
        this.loadedURLs.add(requestedURL.href);
        this.page = Number(requestedURL.searchParams.get('page'));
        this.nextURL = this.validNextURL(incomingRoot.querySelector('[data-ron-pagination] a[rel="next"]')?.href);
        if (!manual) this.automaticRemaining = Math.max(0, this.automaticRemaining - 1);
        this.loading = false;
        this.update();
        this.save();
        if (manual) newProducts[0].querySelector('a')?.focus({ preventScroll: true });
        this.root.dispatchEvent(new CustomEvent('ron:collection:append', {
          bubbles: true, detail: { page: this.page, products: newProducts.length, editorials: newEditorials.length }
        }));
        requestAnimationFrame(() => this.observe());
      } catch (_) {
        if (!this.suspended && this.root.isConnected) this.fail();
      } finally {
        clearTimeout(timeout);
        if (this.controller === controller) this.controller = null;
        this.loading = false;
        this.grid.setAttribute('aria-busy', 'false');
      }
    }

    fail() {
      this.loading = false;
      this.automaticRemaining = 0;
      this.update('The next pieces couldn’t load. Try again or use the next page.');
      this.button.textContent = 'Try again';
      this.fallback.hidden = !this.nextURL;
      this.observer?.disconnect();
    }

    save() {
      if (this.loadedURLs.size === 0) return;
      try {
        const html = this.grid.innerHTML;
        if (html.length > MAX_CACHE_SIZE) { this.removeCache(); return; }
        // Keep one short-lived collection snapshot; storage growth stays bounded.
        for (let index = sessionStorage.length - 1; index >= 0; index--) {
          const key = sessionStorage.key(index);
          if (key?.startsWith(CACHE_PREFIX) && key !== this.cacheKey) sessionStorage.removeItem(key);
        }
        sessionStorage.setItem(this.cacheKey, JSON.stringify({
          time: Date.now(), html, page: this.page, next: this.nextURL?.href || '',
          automaticRemaining: this.automaticRemaining, loadedURLs: [...this.loadedURLs],
          scrollX: scrollX, scrollY: scrollY
        }));
      } catch (_) { /* Native navigation and bfcache still work when storage is unavailable. */ }
    }

    restore() {
      if (!isBackNavigation()) return;
      try {
        const cached = JSON.parse(sessionStorage.getItem(this.cacheKey) || 'null');
        if (!cached || Date.now() - cached.time > CACHE_LIFETIME || cached.html.length > MAX_CACHE_SIZE) return;
        const template = document.createElement('template');
        template.innerHTML = cached.html;
        if (!template.content.querySelector(PRODUCT)) return;
        this.grid.replaceChildren(template.content);
        this.page = cached.page;
        this.nextURL = this.validNextURL(cached.next);
        this.automaticRemaining = Math.max(0, Math.min(2, cached.automaticRemaining));
        this.loadedURLs = new Set(cached.loadedURLs);
        // Rebuild before the browser restores its history position. An extra frame covers
        // browsers that attempted restoration before the appended cards were available.
        if (Number.isFinite(cached.scrollY) && cached.scrollY > 0) {
          requestAnimationFrame(() => requestAnimationFrame(() => scrollTo(cached.scrollX || 0, cached.scrollY)));
        }
      } catch (_) { this.removeCache(); }
    }

    removeCache() {
      try { sessionStorage.removeItem(this.cacheKey); } catch (_) { /* Storage is optional. */ }
    }

    suspend() {
      this.suspended = true;
      this.controller?.abort();
      this.observer?.disconnect();
      this.loading = false;
      this.update();
    }

    resume() {
      this.suspended = false;
      this.loading = false;
      this.update();
      this.observe();
    }

    destroy() {
      this.suspend();
      this.form?.removeEventListener('submit', this.onSubmit);
      instances.delete(this.root);
    }
  }

  const init = scope => {
    scope.querySelectorAll('[data-ron-collection]').forEach(root => {
      if (!instances.has(root)) new CollectionLoading(root);
    });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(document), { once: true });
  else init(document);
  addEventListener('pagehide', () => instances.forEach(instance => { instance.save(); instance.suspend(); }));
  addEventListener('pageshow', event => { if (event.persisted) instances.forEach(instance => instance.resume()); });
  document.addEventListener('shopify:section:load', event => init(event.target));
  document.addEventListener('shopify:section:unload', event => {
    instances.forEach((instance, root) => { if (event.target.contains(root)) instance.destroy(); });
  });
})();
